import { useQuery } from '@tanstack/react-query'

import bootstrapGuestSession from 'utils/bootstrapGuestSession'

import shouldBootstrapGuestOnRefusal from './shouldBootstrapGuestOnRefusal'

const QUERY_KEY = ['guest-session-fallback']


/**
 * Gives an anonymous visitor a guest identity when the server refuses to read
 * their record on a page that has a use for one, then reads it again once the
 * guest's token is stored.
 *
 * The refusal is the server's own say-so that guests come from the identity
 * provider now, so no flag is read here. A browser that is signed in some other
 * way gets a 409 from the guest call, and the record stays refused.
 *
 * @param {Object} args
 * @param {Boolean} [args.isGuestWelcome] - whether the page has a use for a
 *   guest
 * @param {Boolean} [args.isUnauthorized] - whether the server refused to read
 *   the record
 * @param {Function} args.refetch - reads the record again
 *
 * @returns {Object} with the shape { isPending }
 */
export default function useGuestSessionFallback({ isGuestWelcome, isUnauthorized, refetch }) {
  const shouldBootstrap = shouldBootstrapGuestOnRefusal({
    isGuestWelcome,
    isUnauthorized,
  })

  // A query rather than an effect, because the cache outlives this component,
  // so the call is made at most once per page load.
  const {
    isFetching,
    isPending,
  } = useQuery({
    enabled: shouldBootstrap,
    gcTime: Infinity,
    async queryFn() {
      const result = await bootstrapGuestSession()

      // Without a token the server would refuse the record exactly as before.
      if ( result.isTokenStored ) {
        await refetch()
      }

      return result
    },
    queryKey: QUERY_KEY,
    retry: false,
    retryOnMount: false,
    staleTime: Infinity,
  })

  // Pending for as long as the call runs, not only while it is warranted. The
  // call stores a token before it reads the record again, and the stored token
  // makes the call look unwarranted while it is still running. Releasing the
  // page at that point would let the old refusal send the visitor to sign in.
  return {
    isPending: Boolean(isFetching || ( shouldBootstrap && isPending )),
  }
}
